<template>
  <ConfirmModal ref="confirmModal" :title="$t('reviewDesignTitle')" :descriptions="[reviewSubtitle]"
    :primary-button-label="primaryButtonLabel" :secondary-button-label="$t('refineDesign')"
    size="preview" @onConfirm="$emit('onConfirm')" @onCancel="$emit('onClose')">
    <div ref="previewRow" class="tw-flex md:tw-gap-8 tw-gap-3 tw-justify-center tw-items-stretch tw-mt-2">
      <div class="tw-flex tw-flex-col tw-gap-1.5 tw-flex-1 tw-min-w-0">
        <div
          ref="frontPreviewBox"
          class="preview-box tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-shadow-inner"
          :style="previewFrontStyle">
          <img v-if="frontPreviewUrl" :src="frontPreviewUrl" :alt="$t('frontPreview')"
            class="tw-w-full tw-h-full tw-object-cover tw-block" />
        </div>
      </div>

      <div class="tw-flex tw-flex-col tw-gap-1.5 tw-flex-1 tw-min-w-0">
        <div
          class="preview-box tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-shadow-inner"
          :style="previewBackStyle">
          <img v-if="backPreviewUrl" :src="backPreviewUrl" :alt="$t('backPreview')"
            class="tw-w-full tw-h-full tw-object-cover tw-block" />
        </div>
      </div>
    </div>
  </ConfirmModal>
</template>

<script>
import ConfirmModal from './ConfirmModal.vue';
import { canvasAspectRatio, getCanvasBorderRadiusPx } from '@/constants/canvas';

export default {
  name: 'ReviewDesignModal',
  components: { ConfirmModal },
  props: {
    productName: {
      type: String,
      default: 'Shield',
    },
    primaryButtonLabel: {
      type: String,
      default: '',
    },
    frontBackgroundColor: {
      type: String,
      default: '#fff',
    },
    backBackgroundColor: {
      type: String,
      default: '#fff',
    },
  },
  data() {
    return {
      canvasAspectRatio,
      frontPreviewUrl: null,
      backPreviewUrl: null,
      previewBorderRadius: '25px',
      resizeObserver: null,
    };
  },
  computed: {
    reviewSubtitle() {
      return this.$t('reviewDesignSubtitle', { product: this.productName });
    },
    previewFrontStyle() {
      return {
        aspectRatio: canvasAspectRatio,
        borderRadius: this.previewBorderRadius,
        backgroundColor: this.frontBackgroundColor || '#fff',
        '--preview-radius': this.previewBorderRadius,
      };
    },
    previewBackStyle() {
      return {
        aspectRatio: canvasAspectRatio,
        borderRadius: this.previewBorderRadius,
        backgroundColor: this.backBackgroundColor || '#fff',
        '--preview-radius': this.previewBorderRadius,
      };
    },
  },
  beforeUnmount() {
    this.resizeObserver?.disconnect();
  },
  methods: {
    open() {
      this.updatePreviews();
      this.$refs.confirmModal.open();
      this.$nextTick(() => {
        this.updatePreviewRadius();
        setTimeout(() => this.updatePreviewRadius(), 50);
      });
      this.resizeObserver?.disconnect();
      const row = this.$refs.previewRow;
      if (row) {
        this.resizeObserver = new ResizeObserver(() => this.updatePreviewRadius());
        this.resizeObserver.observe(row);
      }
    },
    updatePreviewRadius() {
      const el = this.$refs.frontPreviewBox;
      if (!el?.offsetWidth) return;
      this.previewBorderRadius = `${getCanvasBorderRadiusPx(el.offsetWidth)}px`;
    },
    updatePreviews() {
      if (!window.frontCanvas || !window.backCanvas) return;
      try {
        window.frontCanvas.discardActiveObject();
        window.frontCanvas.renderAll();
        window.backCanvas.discardActiveObject();
        window.backCanvas.renderAll();

        this.frontPreviewUrl = window.frontCanvas.toDataURL({
          format: 'jpeg',
          quality: 0.9,
          multiplier: 1,
          enableRetinaScaling: false,
        });
        this.backPreviewUrl = window.backCanvas.toDataURL({
          format: 'jpeg',
          quality: 0.9,
          multiplier: 1,
          enableRetinaScaling: false,
        });
      } catch (e) {
        console.warn('Could not generate preview:', e);
      }
    },
  },
};
</script>

<style scoped>
.preview-box {
  isolation: isolate;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.preview-box img {
  border-radius: var(--preview-radius, 12px);
  image-rendering: -webkit-optimize-contrast;
}
</style>
